import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useGroup, useGroupMutations, useMemberSearch } from '../../hooks/useGroups';
import { ProgressBar } from '../../components/ProgressBar';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { fmtDateTime, fmtMonthLong, fmtUsd } from '../../lib/format';
import { toolColor } from '../../lib/toolColors';
import type { SpendGroupDetailDto } from '../../lib/types';
import { GroupForm } from './Groups';

export function GroupDetail() {
  const isAdmin = useIsAdmin();
  const { id } = useParams();
  const groupId = Number(id);
  const q = useGroup(Number.isFinite(groupId) ? groupId : null);

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }
  if (q.isLoading) return <div className="text-slate-500">Loading group…</div>;
  if (q.error || !q.data) return <div className="text-red-600 text-sm">Group not found. <Link to="/admin/groups" className="text-atg-blue hover:underline">Back to groups</Link></div>;
  return <GroupDetailInner detail={q.data} />;
}

function GroupDetailInner({ detail }: { detail: SpendGroupDetailDto }) {
  const g = detail.group;
  const toast = useToast();
  const navigate = useNavigate();
  const { update, remove, addMember, removeMember, resend, sendStatus } = useGroupMutations();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState('');
  const results = useMemberSearch(search);
  const memberIds = new Set(detail.members.map(m => `${m.subjectKind}:${m.subjectId}`));

  function errorText(e: unknown, fallback: string) {
    return ((e as ApiError).body as { error?: string } | undefined)?.error ?? fallback;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="text-xs text-slate-500"><Link to="/admin/groups" className="hover:underline">Spend groups</Link> / {g.name}</div>
          <h1 className="text-2xl font-bold text-atg-navy">{g.name}</h1>
          {g.description && <p className="text-slate-600 mt-1 text-sm">{g.description}</p>}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setEditing(true)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Edit</button>
          <button onClick={() => setConfirmDelete(true)} className="rounded-md border border-red-300 text-red-700 px-3 py-1.5 text-sm hover:bg-red-50">Delete</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="bg-white rounded-lg shadow border border-slate-200 p-4 lg:col-span-2 space-y-3">
          <h2 className="text-sm font-semibold text-slate-800">{fmtMonthLong(g.month)} to date</h2>
          <div className="flex flex-wrap gap-6">
            <Stat label="Spent" value={fmtUsd(g.mtdUsd)} />
            <Stat label="Target" value={g.monthlyTargetUsd !== null ? fmtUsd(g.monthlyTargetUsd) : 'none'} />
            <Stat label="Used" value={g.pct !== null ? `${g.pct}%` : '—'} accent={g.pct !== null && g.pct >= 90} />
            <Stat label="Last alert" value={g.lastNotifiedPct !== null ? `${g.lastNotifiedPct}%` : 'none'} sub={g.lastNotifiedAt ? fmtDateTime(g.lastNotifiedAt) : undefined} />
          </div>
          <ProgressBar pct={g.pct} thresholds={g.thresholds} />
          <div className="text-xs text-slate-500">Thresholds: {g.thresholds.map(t => `${t}%`).join(', ') || 'none'} · Admins {g.notifyAdmins ? 'copied' : 'not copied'}</div>
          {detail.mtdByTool.length > 0 && (
            <ul className="flex flex-wrap gap-4 text-sm">
              {detail.mtdByTool.map(t => (
                <li key={t.tool} className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: toolColor(t.tool) }} />
                  <span className="text-slate-600">{t.tool}</span>
                  <span className="tabular-nums font-medium">{fmtUsd(t.amountUsd)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white rounded-lg shadow border border-slate-200 p-4 space-y-2">
          <h2 className="text-sm font-semibold text-slate-800">Emails</h2>
          <p className="text-xs text-slate-500">Group totals only; never individual numbers. Recipients: active developer members{g.notifyAdmins ? ' + admins' : ''}.</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={async () => { try { await sendStatus.mutateAsync(g.id); toast.success('Status email sent.'); } catch (e) { toast.error(errorText(e, 'Could not send.')); } }}
              disabled={sendStatus.isPending}
              className="rounded-md bg-atg-navy text-white px-3 py-1.5 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
            >
              Send status email now
            </button>
            <button
              onClick={async () => { try { const r = await resend.mutateAsync({ id: g.id }); toast.success(`Resent the ${r.thresholdPct}% alert.`); } catch (e) { toast.error(errorText(e, 'Could not resend.')); } }}
              disabled={resend.isPending || g.lastNotifiedPct === null}
              title={g.lastNotifiedPct === null ? 'No alert has fired this month' : undefined}
              className="rounded-md border border-atg-navy text-atg-navy px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              Resend last alert{g.lastNotifiedPct !== null ? ` (${g.lastNotifiedPct}%)` : ''}
            </button>
          </div>
          {detail.recentNotifications.length > 0 && (
            <ul className="text-xs text-slate-600 space-y-1 pt-2 border-t border-slate-100">
              {detail.recentNotifications.slice(0, 6).map(n => (
                <li key={n.id} className={n.error ? 'text-red-600' : ''}>
                  {fmtDateTime(n.sentAt)} · {n.kind === 'Status' ? 'status' : `${n.thresholdPct}% alert`}{n.isManual ? ' (manual)' : ''} · {n.recipients.length} recipients{n.error ? ` · failed: ${n.error}` : ''}
                </li>
              ))}
            </ul>
          )}
          <Link to={`/admin/notifications?groupId=${g.id}`} className="text-xs text-atg-blue hover:underline">All notifications for this group</Link>
        </section>
      </div>

      <section className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-800">Members ({detail.members.length})</h2>
          <div className="ml-auto relative">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Add a developer, API key or workspace…"
              className="w-72 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue"
            />
            {search.trim().length > 0 && results.data && (
              <ul className="absolute right-0 z-20 mt-1 w-80 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow text-sm">
                {results.data.filter(r => !memberIds.has(`${r.subjectKind}:${r.subjectId}`)).map(r => (
                  <li key={`${r.subjectKind}:${r.subjectId}`}>
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-slate-50"
                      onClick={async () => {
                        try {
                          await addMember.mutateAsync({ id: g.id, body: { subjectKind: r.subjectKind, subjectId: r.subjectId } });
                          toast.success(`${r.label} added.`);
                          setSearch('');
                        } catch (e) { toast.error(errorText(e, 'Could not add member.')); }
                      }}
                    >
                      <div className="font-medium">
                        {r.label}
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.subjectKind === 'ApiKey' ? 'API key' : r.subjectKind}</span>
                      </div>
                      <div className="text-xs text-slate-500">{r.detail}</div>
                    </button>
                  </li>
                ))}
                {results.data.filter(r => !memberIds.has(`${r.subjectKind}:${r.subjectId}`)).length === 0 && (
                  <li className="px-3 py-2 text-slate-500">No matches. Developers appear after their first synced spend; API keys and workspaces after the first Claude API sync.</li>
                )}
              </ul>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2">Kind</th>
              <th className="px-4 py-2 text-right">Month to date</th>
              <th className="px-4 py-2 text-right">Share</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {detail.members.map(m => (
              <tr key={m.id} className="border-t border-slate-200">
                <td className="px-4 py-3">
                  <div className="font-medium">{m.label}</div>
                  {m.detail && <div className="text-xs text-slate-500">{m.detail}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">{m.subjectKind}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtUsd(m.mtdUsd)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-600">{g.mtdUsd > 0 ? `${Math.round((m.mtdUsd / g.mtdUsd) * 100)}%` : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={async () => { try { await removeMember.mutateAsync({ id: g.id, memberId: m.id }); toast.info(`${m.label} removed.`); } catch (e) { toast.error(errorText(e, 'Could not remove.')); } }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {detail.members.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No members yet. Search above to add developers.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <Modal open={editing} onClose={() => setEditing(false)} title={`Edit ${g.name}`}>
        <GroupForm
          initial={{ name: g.name, description: g.description, monthlyTargetUsd: g.monthlyTargetUsd, thresholds: g.thresholds, notifyAdmins: g.notifyAdmins }}
          submitLabel="Save"
          pending={update.isPending}
          onSubmit={async body => {
            await update.mutateAsync({ id: g.id, body });
            toast.success('Group updated.');
            setEditing(false);
          }}
        />
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        destructive
        title={`Delete ${g.name}?`}
        message="Members are not affected; their spend stays. Notifications already sent remain in the log."
        confirmLabel="Delete group"
        onConfirm={async () => {
          try { await remove.mutateAsync(g.id); toast.success('Group deleted.'); navigate('/admin/groups'); }
          catch (e) { toast.error(errorText(e, 'Could not delete.')); }
        }}
      />
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${accent ? 'text-atg-orange' : 'text-slate-900'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
